import React, { useState } from 'react';
import toast from 'react-hot-toast';
import CardButton from './CardButton';
import StatsWrapper from '../../utils/StatsWrapper';
import AmazonLink from './AmazonLink';
import EmojiFlag from '../emoji/EmojiFlag';
import ShippingStatusBadge from '../ShippingStatusBadge';
import amazonDomainFlags from '../../utils/amazonDomainFlags';
import { ShoppingCart, ChevronDown, ChevronUp, Maximize2, Info } from 'lucide-react';
import { isLoggedIn } from '../../utils/userUtils';
import Spinner from '../common/Spinner';

/**
 * ProductCard component for displaying item information with deal analysis
 * @param {Object} props - Component props
 * @param {string} props.title - Product title (may be truncated for display)
 * @param {string} props.fullTitle - Full product title (untruncated)
 * @param {string} props.primaryImage - Product image URL
 * @param {Array} props.locales - Array of locale/domain data
 * @param {string} props.asin - Product ASIN
 * @param {Function} props.onSearchVariants - Callback for searching variants
 * @param {Object} props.dealAnalysis - Deal analysis data (now comes from item data)
 * @param {boolean|null} props.freeShippingToIsrael - Free shipping status (from API)
 * @param {number|null} props.shippingCostToIsrael - Shipping cost (from API)
 */
const ProductCard = ({
  title,
  fullTitle,
  primaryImage,
  locales,
  asin,
  onSearchVariants,
  dealAnalysis,
  freeShippingToIsrael,
  shippingCostToIsrael,
  isTracking,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Find amazon.com locale for shipping status
  const amazonComLocale = locales?.find((locale) => locale.domain === 'com');

  /**
   * Toggle expansion state for item details
   * @param {number} index - Item index to toggle
   */
  const toggleItemExpand = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (!locales?.length) {
    return (
      <div className="border rounded-lg shadow-md p-4 bg-white">
        <p>Product information unavailable</p>
      </div>
    );
  }

  /**
   * Handle track button click - opens tracking modal or auth modal
   */
  const handleTrackClick = async () => {
    setLoading(true);
    StatsWrapper.track('item.track_button_clicked');

    if (!isLoggedIn()) {
      // If user is not logged in, show a message and trigger the auth modal
      toast('יש להתחבר תחילה כדי לעקוב אחר מחירים', {
        duration: 3000,
        position: 'bottom-center',
      });

      // Dispatch a custom event to open the auth modal
      const event = new CustomEvent('openAuthModal');
      document.dispatchEvent(event);
    } else {
      // User is logged in, open track modal
      // Get the cheapest locale for tracking
      const cheapestLocale = locales.sort(
        (a, b) => (a.priceIls || Infinity) - (b.priceIls || Infinity)
      )[0];

      // Prepare item data for the track modal
      const itemToTrack = {
        id: asin,
        name: title,
        currentPrice: cheapestLocale?.price || 0,
        image: primaryImage,
        currency: cheapestLocale?.currency || '₪',
        domain: cheapestLocale?.domain,
        pageURL: cheapestLocale?.pageURL,
      };

      // Dispatch event to open track modal with item data
      const event = new CustomEvent('openTrackModal', { detail: itemToTrack });
      document.dispatchEvent(event);
    }

    setLoading(false);
  };

  /**
   * Handle variants button click - searches for product variants
   */
  const handleVariantsClick = async () => {
    setLoadingVariants(true);
    await onSearchVariants(asin);
    setLoadingVariants(false);
  };

  /**
   * Handle enlarge button click - opens fullscreen modal
   */
  const handleEnlargeClick = () => {
    StatsWrapper.track('item.enlarge_clicked', {
      asin: asin,
      title: fullTitle || title,
    });

    // Dispatch event to open fullscreen modal with specific ASIN
    const event = new CustomEvent('openItemFullscreen', {
      detail: { asin: asin },
    });
    document.dispatchEvent(event);
  };

  // Rows are sorted by price (unpriced rows sort first), so the image links to the first
  // row that actually has a price rather than a "price unavailable" dead end.
  const imageLinkLocale = [...locales]
    .sort((a, b) => (a.priceIls || 0) - (b.priceIls || 0))
    .find((l) => l.pageURL && l.displayAmount != null && l.priceIls);

  const goodDealMessage =dealAnalysis?.isGoodDeal ? dealAnalysis.message : '';

  return (
    <>
      {/* ItemFullscreenModal is no longer mounted here - a single shared instance
          lives in SearchResults.jsx (keyed by whichever ASIN is currently open), not
          one per card. See ItemFullscreenModal.jsx for why. This card only dispatches
          the `openItemFullscreen` event (handleEnlargeClick above) with its own asin. */}
      <div
        className={`border rounded-lg shadow-md p-4 bg-white relative overflow-hidden ${
          dealAnalysis?.isGoodDeal
            ? 'border-2 border-green-400 shadow-lg shadow-green-100 ring-2 ring-green-200'
            : 'border-gray-200'
        }`}
      >
        {/* Enlarge button - top right corner */}
        <button
          onClick={handleEnlargeClick}
          className="enlarge-button absolute top-2 right-2 z-20 bg-white/90 hover:bg-white 
          text-gray-700 hover:text-gray-900 p-2 rounded-lg shadow-md hover:shadow-lg 
          transition-all duration-200 transform hover:scale-105 active:scale-95 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          title="הצג בתצוגה מלאה"
        >
          <Maximize2 size={16} />
        </button>

        {/* Unified deal banner */}
        {dealAnalysis?.isGoodDeal && (
          <div className="unified-deal-banner absolute top-2 left-2 right-12 z-10">
            <div className="bg-black/20 backdrop-blur-md text-white text-xs font-bold px-3 py-2 rounded-lg shadow-xl border border-white/20">
              <div className="flex items-center justify-center gap-2 whitespace-nowrap overflow-hidden">
                <span className="text-white text-xs font-medium drop-shadow-sm truncate" dir="rtl">
                  {goodDealMessage}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col h-full">
          {imageLinkLocale ? (
            <AmazonLink
              href={imageLinkLocale.pageURL}
              eventName="item.image_link_clicked"
              eventProperties={{ asin, domain: imageLinkLocale.domain }}
              dealFollowupItemName={fullTitle || title}
              className="block mb-4 cursor-pointer"
            >
              <img src={primaryImage} alt={title} className="w-full h-48 object-contain" />
            </AmazonLink>
          ) : (
            <img src={primaryImage} alt={title} className="w-full h-48 object-contain mb-4" />
          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-2 cursor-help" title={title}>
            {title.length > 50 ? `${title.substring(0, 50)}...` : title}
          </h3>

          {/* Shipping Status Badge - only show if item has shipping data */}
          {freeShippingToIsrael !== undefined &&
            freeShippingToIsrael !== null && (
              <div className="mb-2">
                <ShippingStatusBadge
                  freeShippingToIsrael={freeShippingToIsrael}
                  shippingCostToIsrael={shippingCostToIsrael}
                  domain={amazonComLocale?.domain || 'com'}
                />
              </div>
            )}

          {/* Tracking Status Badge */}
          {isTracking && (
            <div className="mb-2">
              <div
                className="tracking-status-badge"
                dir="rtl"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  color: '#92400e',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#f59e0b',
                  }}
                />
                <span>במעקב</span>
              </div>
            </div>
          )}

          <div className="space-y-2 item-rows">
            {locales
              .sort((a, b) => (a.priceIls || 0) - (b.priceIls || 0))
              .map((data, index) => getItemRow(data, index, expandedItems, toggleItemExpand, asin, fullTitle || title))}
          </div>
          <div className="flex-grow" />
          <div className="flex gap-2 mt-4 card-buttons">
            <CardButton
              text={loadingVariants ? <Spinner variant="button" /> : 'גרסאות נוספות'}
              onClick={handleVariantsClick}
              disabled={loadingVariants}
              className="variants-button"
            />
            <CardButton
              text={loading ? <Spinner variant="button" /> : isTracking ? 'במעקב' : 'הרשמה למעקב'}
              onClick={handleTrackClick}
              disabled={loading || isTracking}
              className={`track-button ${isTracking ? 'opacity-75 cursor-not-allowed' : ''}`}
            />
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Render individual item row with expandable details
 * @param {Object} data - Item locale data
 * @param {number} index - Item index
 * @param {Object} expandedItems - Expanded items state
 * @param {Function} toggleItemExpand - Toggle expansion function
 * @returns {JSX.Element} Item row component
 */
function getItemRow(data, index, expandedItems, toggleItemExpand, asin, itemName) {
  const isExpanded = expandedItems[index] || false;

  return (
    <div
      key={data.locale}
      className={`flex flex-col border-b last:border-b-0 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
    >
      <div className="flex items-center justify-between">
        <AmazonLink
          href={data.pageURL}
          eventName="item.purchase_link_clicked"
          eventProperties={{ asin, domain: data.domain }}
          dealFollowupItemName={itemName}
          className="flex items-center gap-2 bg-stone-200 hover:bg-stone-300
            text-stone-700 font-medium py-1.5 px-3 rounded-md transition-all duration-300 ease-in-out
            transform hover:scale-102 hover:shadow-sm active:scale-98 focus:outline-none focus:ring-2
            focus:ring-stone-300 focus:ring-opacity-50"
        >
          <ShoppingCart size={14} />
        </AmazonLink>
        <p className="text-gray-800 text-sm ml-4 flex-grow" dir="rtl">
          {renderPurchaseLink(data, asin, itemName)} ב {data.displayAmount ?? 'מחיר לא זמין'}
          {data.finalPriceIls != null && (
            <span style={{ color: '#3730a3', fontWeight: 700, marginRight: '6px' }}>
              {' '}מחיר סופי {Math.round(data.finalPriceIls).toLocaleString()}₪
              <span className="relative inline-flex group" style={{ verticalAlign: 'middle', marginRight: '3px' }}>
                <button className="inline-flex items-center focus:outline-none" onClick={(e) => e.stopPropagation()}>
                  <Info size={11} className="text-indigo-400 cursor-help" style={{ marginRight: '2px' }} />
                </button>
                <span className="pointer-events-none absolute bottom-full left-0 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded w-44 z-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150" dir="rtl">
                  מחיר סופי כולל הכל! כולל משלוח ומיסים מראש
                </span>
              </span>
            </span>
          )}
        </p>
        <button
          onClick={() => toggleItemExpand(index)}
          className="flex-shrink-0 cursor-pointer text-gray-400 hover:text-gray-600 transition-all duration-200 rounded-full hover:bg-gray-100 p-1"
        >
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      {isExpanded && (
        <div className="p-3 text-black text-sm rounded-lg border-t border-gray-100 mt-1">
          <p className="whitespace-nowrap">נמכר ע"י: {data.merchantName || 'לא צוין'}</p>
          <p className="whitespace-nowrap">מצב: {data.condition || 'לא צוין'}</p>
          <p className="whitespace-nowrap">נשלח מ{amazonDomainFlags[data.domain] || data.domain}</p>
          <br />
          <p className="whitespace-normal">
            המחיר המצויין לא כולל משלוח ומיסים, היכנסו והתקדמו לסל כדי לראות מחיר סופי
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Format item condition for display
 * @param {string} condition - Item condition
 * @returns {string} Formatted condition string
 */
function getItemCondition(condition) {
  if (!condition) return 'לא צוין';
  return condition.toLowerCase() === 'new' ? '' : `${condition}, `;
}

/**
 * Render purchase link with tracking
 * @param {Object} data - Item locale data
 * @returns {JSX.Element} Purchase link component
 */
function renderPurchaseLink(data, asin, itemName) {
  return (
    <AmazonLink
      href={data.pageURL}
      eventName="item.purchase_link_clicked"
      eventProperties={{ asin, domain: data.domain }}
      dealFollowupItemName={itemName}
      className="text-sm hover:opacity-75 whitespace-nowrap"
    >
      <EmojiFlag tld={data.domain} />
    </AmazonLink>
  );
}

/**
 * Format ILS price for display
 * @param {Object} data - Item locale data
 * @returns {string} Formatted ILS price string
 */
const getPriceIls = (data) => {
  if (data.priceIls) {
    return `(${Number(data.priceIls).toFixed(2)}₪)`;
  }
  return '';
};

export default ProductCard;
